(function () {
  // ============================================
  // mask-wrap スクリプト（行数制限 + 「さらに表示」）
  //   - 本文コンテナは .mask-content（固定）
  //   - クランプ状態は .text-clamp（ON/OFF）
  // ============================================

  const SELECTOR       = '.mask-wrap';
  const CONTENT_CLASS  = 'mask-content';     // ★固定の本文コンテナ
  const CLAMP_CLASS    = 'text-clamp';       // ★状態クラス（付け外し）
  const BTN_CLASS      = 'readmore';
  const DEFAULT_LINES  = 3;
  const STORE_NS       = 'textClampExpanded:';
  const PC_MIN_WIDTH   = 759;

  const debounce = (fn, wait = 150) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  function hash(str) {
    let h = 5381, i = str.length;
    while (i) h = (h * 33) ^ str.charCodeAt(--i);
    return (h >>> 0).toString(36);
  }

  function currentTargetActive(target) {
    const w = window.innerWidth || document.documentElement.clientWidth;
    if (target === 'sp') return w < PC_MIN_WIDTH;
    if (target === 'pc') return w >= PC_MIN_WIDTH;
    if (target === 'none') return false;
    return true;
  }

  function normalizeTarget(val) {
    const v = (val || '').toLowerCase();
    return ['sp','pc','both','none'].includes(v) ? v : 'both';
  }

  function resolveKey(wrap, contentEl, target, lines) {
    if (wrap.dataset.maskKey) return wrap.dataset.maskKey + `|${target}|${lines}`;
    const pid = wrap.closest('[data-product-id]')?.getAttribute('data-product-id');
    if (pid) return `pid:${pid}|${target}|${lines}`;
    const text = (contentEl?.textContent || '').trim().slice(0, 200);
    return `t:${hash(text)}|${target}|${lines}`;
  }

  // ★本文コンテナを必ず1つだけにする
  function getOrBuildContent(wrap) {
    // 既存の本文コンテナがあれば使う
    let content = wrap.querySelector(':scope > .' + CONTENT_CLASS);
    if (content) return content;

    // すでに「過去のネスト事故」が起きている場合に備え、最初の要素が div 連鎖ならほどく
    // （安全寄り：button.readmore は除外しつつ、先頭の div 連鎖を1段だけ剥がす）
    const first = wrap.firstElementChild;
    if (first && first.tagName === 'DIV' && !first.classList.contains(BTN_CLASS) && first.querySelector(':scope > div')) {
      // 連鎖している場合、最外をwrap直下に戻す
      while (wrap.firstElementChild && wrap.firstElementChild.tagName === 'DIV' && wrap.firstElementChild.children.length === 1 && wrap.firstElementChild.firstElementChild.tagName === 'DIV') {
        const outer = wrap.firstElementChild;
        const inner = outer.firstElementChild;
        // innerをouterの外へ
        wrap.insertBefore(inner, outer);
        outer.remove();
      }
    }

    content = document.createElement('div');
    content.className = CONTENT_CLASS;

    const toMove = [];
    wrap.childNodes.forEach(node => {
      if (node.nodeType === 1 && node.classList?.contains(BTN_CLASS)) return;
      toMove.push(node);
    });

    toMove.forEach(node => content.appendChild(node));
    wrap.insertBefore(content, wrap.firstChild);

    return content;
  }

  function measureLinesUnclamped(contentEl) {
    const rect = contentEl.getBoundingClientRect();
    const width = rect.width || contentEl.clientWidth || 0;
    if (!width) return null;

    const cs = getComputedStyle(contentEl);
    let lineHeight = parseFloat(cs.lineHeight);
    if (Number.isNaN(lineHeight)) lineHeight = parseFloat(cs.fontSize) * 1.2;

    const clone = contentEl.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.pointerEvents = 'none';
    clone.style.whiteSpace = 'normal';
    clone.style.display = 'block';
    clone.style.width = width + 'px';
    clone.style.maxHeight = 'none';

    // 計測用はクランプ状態を完全解除
    clone.classList.remove(CLAMP_CLASS);
    clone.style.removeProperty('-webkit-line-clamp');
    clone.style.webkitLineClamp = 'unset';
    clone.style.removeProperty('--mask-lines');

    const tmp = document.createElement('div');
    tmp.style.position = 'absolute';
    tmp.style.inset = '0';
    tmp.style.visibility = 'hidden';
    tmp.style.pointerEvents = 'none';
    tmp.appendChild(clone);
    document.body.appendChild(tmp);

    const h = clone.scrollHeight || clone.getBoundingClientRect().height;
    tmp.remove();

    return Math.max(1, Math.round(h / lineHeight));
  }

  function addButton(wrap, contentEl, storageKey, btnLabel = 'さらに表示') {
    wrap.querySelector('.' + BTN_CLASS)?.remove();

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = BTN_CLASS;
    btn.textContent = btnLabel;

    btn.addEventListener('click', () => {
      contentEl.classList.remove(CLAMP_CLASS);
      contentEl.style.removeProperty('--mask-lines');
      contentEl.style.removeProperty('-webkit-line-clamp');
      contentEl.style.webkitLineClamp = '';

      sessionStorage.setItem(STORE_NS + storageKey, '1');
      btn.remove();
    });

    wrap.appendChild(btn);
  }

  function setLines(contentEl, maxLines) {
    contentEl.style.setProperty('--mask-lines', String(maxLines));
    contentEl.style.setProperty('-webkit-line-clamp', String(maxLines));
    contentEl.style.webkitLineClamp = String(maxLines);
  }

  const retryMap = new WeakMap();
  function scheduleRetry(wrap) {
    const count = retryMap.get(wrap) || 0;
    if (count >= 10) return;
    retryMap.set(wrap, count + 1);
    setTimeout(() => applyClamp(wrap), 120);
  }

  function applyClamp(wrap) {
    if (!(wrap instanceof Element)) return;

    const contentEl = getOrBuildContent(wrap);
    const target = normalizeTarget(wrap.dataset.maskTarget);
    const active = currentTargetActive(target);
    const maxLines = parseInt(wrap.dataset.maskLines, 10) || DEFAULT_LINES;

    if (!active) {
      contentEl.classList.remove(CLAMP_CLASS);
      wrap.querySelector('.' + BTN_CLASS)?.remove();
      return;
    }

    const storageKey = resolveKey(wrap, contentEl, target, maxLines);
    const wasExpanded = sessionStorage.getItem(STORE_NS + storageKey) === '1';

    const lines = measureLinesUnclamped(contentEl);
    if (lines === null) {
      scheduleRetry(wrap);
      return;
    }

    setLines(contentEl, maxLines);

    if (lines > maxLines) {
      if (wasExpanded) {
        contentEl.classList.remove(CLAMP_CLASS);
        wrap.querySelector('.' + BTN_CLASS)?.remove();
      } else {
        contentEl.classList.add(CLAMP_CLASS);
        addButton(wrap, contentEl, storageKey);
      }
    } else {
      contentEl.classList.remove(CLAMP_CLASS);
      wrap.querySelector('.' + BTN_CLASS)?.remove();
    }
  }

  function initExisting() {
    document.querySelectorAll(SELECTOR).forEach(applyClamp);
  }

  function observeMutations() {
    const mo = new MutationObserver((muts) => {
      muts.forEach(m => {
        m.addedNodes && m.addedNodes.forEach(node => {
          if (!(node instanceof Element)) return;
          if (node.matches?.(SELECTOR)) applyClamp(node);
          node.querySelectorAll?.(SELECTOR).forEach(applyClamp);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function observeResize() {
    if (!('ResizeObserver' in window)) return;
    const ro = new ResizeObserver((entries) => {
      entries.forEach(e => applyClamp(e.target));
    });
    document.querySelectorAll(SELECTOR).forEach(wrap => ro.observe(wrap));
  }

  const onResize = debounce(() => {
    document.querySelectorAll(SELECTOR).forEach(applyClamp);
  }, 150);

  function boot() {
    initExisting();
    requestAnimationFrame(initExisting);
    observeMutations();
    observeResize();
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('load', initExisting, { passive: true, once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();